import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import Select from "react-select"; // Import react-select for the state dropdown
import { FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { useCreateItem } from "../../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../../_helper/constant/api-types";
import { useItemsFilter } from "../../../_helper/actions/app/items";
import { States } from "../../../_helper/constant/States";
import { useNavigate } from "react-router-dom";

const PharmacyOnboardingForm = () => {
  const [formData, setFormData] = useState({
    pharmacy_name: "",
    license_no: "",
    type: "Retail",
    address: "",
    state: "", // we'll update this via react-select
    city: "",
    zip_code: "",
    pharmacy_contact_number: "",
    pharmacy_email: "",
    authorized_person_name: "",
    authorized_person_contact: "",
    authorized_person_email: "",
    is_GST_applicable: false,
    gstin_number: "",
    inward_calculated_on_mrp: false,
    inward_calculate_on_wholesale_price: true,
  });

  const [stakeholders, setStakeholders] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creatingNewUser, setCreatingNewUser] = useState(false);
  const [searchUserInput, setSearchUserInput] = useState("");
  const navigate = useNavigate();

  // Only perform search if the mobile number has exactly 10 characters
  const shouldSearch = searchUserInput.length === 10;
  const searchQuery = shouldSearch
    ? `?mobile=${searchUserInput}&is_pharmacist=true`
    : "";

  // Call your custom hook to fetch users matching the mobile number only if shouldSearch is true.
  const { data: serachMobileNo } = useItemsFilter(
    ApiEndPoint.USERS_SEARCH,
    searchQuery
  );

  // Whenever the search result changes, update the users list only if we are in search mode.
  useEffect(() => {
    if (shouldSearch && serachMobileNo && Array.isArray(serachMobileNo)) {
      setUsers(serachMobileNo);
    } else {
      setUsers([]);
    }
  }, [serachMobileNo, shouldSearch]);

  // New user state – note the keys are now using underscores to match backend payload
  const [newUser, setNewUser] = useState({
    mobile: "",
    first_name: "",
    last_name: "",
    password1: "",
    password2: "",
  });

  const mutation = useCreateItem(ApiEndPoint.PHARMACY_ONBOARDING);

  // Handle changes in main form fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle changes in new user creation fields
  const handleNewUserChange = (e) => {
    setNewUser((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Called when a user selects an existing option from the CreatableSelect
  const handleUserChange = (option) => {
    setSelectedUser(option);
  };

  // Called when the user types in a mobile number that isn’t found.
  // (This will only be available when the input length is exactly 10.)
  const handleCreateUserOption = (inputValue) => {
    setCreatingNewUser(true);
    setNewUser((prev) => ({
      ...prev,
      mobile: inputValue,
    }));
    setSelectedUser(null);
  };

  // Handle stakeholder field changes
  const handleStakeholderChange = (index, e) => {
    const { name, value } = e.target;
    const updatedStakeholders = [...stakeholders];
    updatedStakeholders[index][name] = value;
    setStakeholders(updatedStakeholders);
  };

  const addStakeholder = () => {
    setStakeholders((prev) => [...prev, { name: "", share: "" }]);
  };

  const removeStakeholder = (index) => {
    setStakeholders((prev) => prev.filter((_, i) => i !== index));
  };

  // Validate that required fields are filled in
  const validateForm = () => {
    // Validate formData fields (except license_no)
    if (!formData.pharmacy_name.trim()) {
      toast.error("Pharmacy Name is required.");
      return false;
    }
    if (!formData.address.trim()) {
      toast.error("Street Address is required.");
      return false;
    }
    if (!formData.state) {
      toast.error("State is required.");
      return false;
    }
    if (!formData.city.trim()) {
      toast.error("City is required.");
      return false;
    }
    if (!formData.zip_code.trim()) {
      toast.error("Zip Code is required.");
      return false;
    }
    if (!formData.pharmacy_contact_number.trim()) {
      toast.error("Contact Number is required.");
      return false;
    }
    if (!formData.pharmacy_email.trim()) {
      toast.error("Pharmacy Email is required.");
      return false;
    }
    if (!formData.authorized_person_name.trim()) {
      toast.error("Authorized Person Name is required.");
      return false;
    }
    if (!formData.authorized_person_contact.trim()) {
      toast.error("Authorized Person Contact is required.");
      return false;
    }
    if (!formData.authorized_person_email.trim()) {
      toast.error("Authorized Person Email is required.");
      return false;
    }
    if (formData.is_GST_applicable && !formData.gstin_number.trim()) {
      toast.error("GST Number is required when GST is applicable.");
      return false;
    }
    // If creating a new user, validate the newUser fields
    if (creatingNewUser) {
      if (!newUser.mobile.trim()) {
        toast.error("Mobile number is required.");
        return false;
      }
      if (!newUser.first_name.trim()) {
        toast.error("First Name is required.");
        return false;
      }
      if (!newUser.last_name.trim()) {
        toast.error("Last Name is required.");
        return false;
      }
      if (!newUser.password1) {
        toast.error("Password is required.");
        return false;
      }
      if (!newUser.password2) {
        toast.error("Confirm Password is required.");
        return false;
      }
      if (newUser.password1 !== newUser.password2) {
        toast.error("Passwords do not match!");
        return false;
      }
    }
    return true;
  };

  // Handle form submission and create payload accordingly
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser && !creatingNewUser) {
      toast.error("Please select an existing user or create a new user.");
      return;
    }

    // Validate required fields before proceeding
    if (!validateForm()) {
      return;
    }

    const backend_permissions = [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
      22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
      40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57,
      58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75,
      76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93,
      94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109,
      110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124,
      125, 126, 127, 128, 129, 130, 131, 132, 133, 134, 135, 136, 137, 138, 139,
      140, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151, 152, 153, 154,
      155, 156, 157, 158, 159, 160, 161, 162, 163, 164, 165, 166, 167, 168, 169,
      170, 171, 172, 173, 174, 175, 176, 177, 178, 179, 180, 181, 182, 183, 184,
      185, 186, 187, 188, 189, 190, 191, 192, 193, 194, 195, 196, 197, 198, 199,
      200,
    ];

    const frontend_permissions = [2500, 2520, 2521, 2522, 2523, 8000];

    // Build the payload according to backend specifications.
    const payload = {
      ...(selectedUser
        ? { id: selectedUser.value }
        : {
            first_name: newUser.first_name,
            last_name: newUser.last_name,
            mobile: newUser.mobile,
            password1: newUser.password1,
            password2: newUser.password2,
          }),
      is_pharmacist: true,
      is_internal: false,
      linked_pharmacy: [
        {
          pharmacy_name: formData.pharmacy_name,
          type: formData.type,
          pharmacy_contact_number: formData.pharmacy_contact_number,
          pharmacy_email: formData.pharmacy_email,
          gstin_number: formData.gstin_number,
          city: formData.city,
          state: formData.state, // now set via the React Select
          zip_code: formData.zip_code,
          address: formData.address,
          is_GST_applicable: formData.is_GST_applicable,
          is_standalone_pharmacy: true,
          authorized_person_name: formData.authorized_person_name,
          authorized_person_contact: formData.authorized_person_contact,
          authorized_person_email: formData.authorized_person_email,
          inward_calculated_of_on_mrp: formData.inward_calculated_on_mrp,
          inward_calculate_on_wholesale_price:
            formData.inward_calculate_on_wholesale_price,
          clinic_id: null,
        },
      ],
      roles: [
        {
          name: "Pharmacist Admin",
          questions: null,
          permissions: backend_permissions,
          frontend_permissions: frontend_permissions,
          is_admin: true,
          staff_name: selectedUser ? selectedUser.label : newUser.first_name,
          is_accepted_appointment: false,
          is_verified: true,
          description:
            "Pharmacist role with access to prescriptions and inventory.",
          is_organization_level: false,
          is_system_generated: true,
        },
      ],
      language: "English",
    };

    console.log("Payload:", payload);
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success("Pharmacy onboarded successfully now you can login!");
        navigate("/login");
      },
      onError: (err) => {
        console.log(err);
        toast.error(err.response.data.message);
      },
    });
  };

  // Custom styles for react-select (light theme)
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "white",
      borderColor: "#d1d5db",
      color: "#374151",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#374151",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "white",
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor: isFocused ? "#e5e7eb" : "white",
      color: "#374151",
    }),
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6">
      <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-md w-full max-w-4xl">
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
          New Pharmacy Onboarding
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pharmacy Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Pharmacy Name*
              </label>
              <input
                type="text"
                name="pharmacy_name"
                value={formData.pharmacy_name}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Pharmacy Name"
              />
            </div>

            {/* License Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                License No
              </label>
              <input
                type="text"
                name="license_no"
                value={formData.license_no}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter License No"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type*
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
              >
                <option value="Retail">Retail</option>
                <option value="Wholesale">Wholesale</option>
              </select>
            </div>

            {/* Street Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Street Address*
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Address"
              />
            </div>

            {/* Zip Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Zip Code*
              </label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Zip Code"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                City*
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter City"
              />
            </div>

            {/* State Select using React Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                State*
              </label>
              <Select
                options={States}
                value={
                  States.find((option) => option.value === formData.state) ||
                  null
                }
                onChange={(option) =>
                  setFormData((prev) => ({ ...prev, state: option.value }))
                }
                placeholder="Select State"
                styles={customSelectStyles}
              />
            </div>

            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact Number*
              </label>
              <input
                type="text"
                name="pharmacy_contact_number"
                value={formData.pharmacy_contact_number}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Contact Number"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Pharmacy Email*
              </label>
              <input
                type="email"
                name="pharmacy_email"
                value={formData.pharmacy_email}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Pharmacy Email"
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Authorized Person Name*
              </label>
              <input
                type="text"
                name="authorized_person_name"
                value={formData.authorized_person_name}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Authorized Person Name"
              />
            </div>

            {/* authorized_person_contact */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Authorized Person Contact*
              </label>
              <input
                type="text"
                name="authorized_person_contact"
                value={formData.authorized_person_contact}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Authorized Person Contact"
              />
            </div>
          </div>

          {/* GST Applicability */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="is_GST_applicable"
              checked={formData.is_GST_applicable}
              onChange={handleInputChange}
              className="form-checkbox h-5 w-5 text-blue-500"
            />
            <label className="ml-2 text-sm text-gray-700">GST Applicable</label>
          </div>

          {formData.is_GST_applicable && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                GST Number
              </label>
              <input
                type="text"
                name="gstin_number"
                value={formData.gstin_number}
                onChange={handleInputChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter GST Number"
              />
            </div>
          )}

          {/* Stakeholders Section */}
          <h3 className="text-lg font-semibold text-gray-800">Stakeholders</h3>
          {stakeholders.map((partner, index) => (
            <div key={index} className="flex gap-4 items-center">
              <input
                type="text"
                name="name"
                value={partner.name}
                onChange={(e) => handleStakeholderChange(index, e)}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Name"
              />
              <input
                type="number"
                name="share"
                value={partner.share}
                onChange={(e) => handleStakeholderChange(index, e)}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Share (%)"
              />
              <FaTrash
                className="text-red-500 cursor-pointer"
                onClick={() => removeStakeholder(index)}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addStakeholder}
            className="flex items-center text-blue-500"
          >
            <FaPlus className="mr-2" /> Add Stakeholder
          </button>

          {/* User Selection Section */}
          <h3 className="text-lg font-semibold text-gray-800">
            Assign First User*
          </h3>
          {!creatingNewUser ? (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select or Create a User by Mobile Number
              </label>
              <CreatableSelect
                isClearable
                // Only show options when the input length is exactly 10.
                options={
                  searchUserInput.length === 10
                    ? users.map((user) => ({
                        value: user.id,
                        label: user.mobile,
                      }))
                    : []
                }
                value={selectedUser}
                onChange={handleUserChange}
                // Only allow create if the input length is 10 and no user is found.
                onCreateOption={
                  searchUserInput.length === 10 && users.length === 0
                    ? handleCreateUserOption
                    : undefined
                }
                placeholder="Type a mobile number..."
                styles={customSelectStyles}
                inputValue={searchUserInput}
                onInputChange={(newValue) => setSearchUserInput(newValue)}
              />
            </>
          ) : (
            <>
              <label className="block text-sm font-medium text-gray-700">
                Mobile Number*
              </label>
              <input
                type="text"
                name="mobile"
                value={newUser.mobile}
                onChange={handleNewUserChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Mobile Number"
                disabled
              />
              <label className="block text-sm font-medium text-gray-700 mt-2">
                First Name*
              </label>
              <input
                type="text"
                name="first_name"
                value={newUser.first_name}
                onChange={handleNewUserChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter First Name"
              />
              <label className="block text-sm font-medium text-gray-700 mt-2">
                Last Name*
              </label>
              <input
                type="text"
                name="last_name"
                value={newUser.last_name}
                onChange={handleNewUserChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Last Name"
              />
              <label className="block text-sm font-medium text-gray-700 mt-2">
                Password*
              </label>
              <input
                type="password"
                name="password1"
                value={newUser.password1}
                onChange={handleNewUserChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Enter Password"
              />
              <label className="block text-sm font-medium text-gray-700 mt-2">
                Confirm Password*
              </label>
              <input
                type="password"
                name="password2"
                value={newUser.password2}
                onChange={handleNewUserChange}
                className="w-full p-2 bg-white text-gray-900 border border-gray-300 rounded-md"
                placeholder="Confirm Password"
              />
              {newUser.password1 &&
                newUser.password2 &&
                newUser.password1 !== newUser.password2 && (
                  <p className="text-red-500 text-sm mt-1">
                    Passwords do not match!
                  </p>
                )}
              <div className="flex justify-between mt-3">
                <div
                  className="text-red-500 text-sm cursor-pointer self-center"
                  onClick={() => {
                    setNewUser({
                      mobile: "",
                      first_name: "",
                      last_name: "",
                      password1: "",
                      password2: "",
                    });
                    setCreatingNewUser(false);
                  }}
                >
                  - Cancel
                </div>
              </div>
            </>
          )}

          <button
            type="submit"
            className="bg-blue-500 text-white w-full py-2 rounded-lg font-semibold"
          >
            Onboard Pharmacy
          </button>
        </form>
      </div>
    </div>
  );
};

export default PharmacyOnboardingForm;
